import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifySession();
  }, []);

  const verifySession = async () => {
    try {
      const response = await ApiService.verifyToken();
      if (response.success) {
        setUser(response.user);
      }
    } catch (error) {
      console.log('No active session');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await ApiService.login({ username, password });
      if (response.success) {
        setUser(response.user);
        return { success: true, user: response.user, pendingApproval: response.pendingApproval };
      }
      return { success: false, error: 'Invalid username or password' };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await ApiService.register(userData);
      if (response.success) {
        setUser(response.user);
        return {
          success: true,
          user: response.user,
          pendingApproval: response.pendingApproval,
          message: response.message
        };
      }
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const loginWithGoogle = async (payload) => {
    try {
      const response = await ApiService.googleLogin(payload);
      if (response.success) {
        setUser(response.user);
        return {
          success: true,
          user: response.user,
          pendingApproval: response.pendingApproval,
          needsContributorProfile: false
        };
      }
      return { success: false, error: 'Google sign-in failed' };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Google sign-in failed',
        needsContributorProfile: !!(error.data && error.data.needsContributorProfile),
        googleProfile: error.data?.googleProfile
      };
    }
  };

  const logout = async () => {
    try {
      await ApiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await ApiService.verifyToken();
      if (response.success) {
        setUser(response.user);
        return response.user;
      }
    } catch (error) {
      setUser(null);
    }
    return null;
  };

  const isAdmin = user?.role === 'admin';
  const isPendingContributor = user?.role === 'author' && user?.status === 'pending';
  const isAuthor = (user?.role === 'author' && user?.status === 'active') || isAdmin;
  const isLoggedIn = !!user;

  const value = {
    user,
    login,
    register,
    loginWithGoogle,
    logout,
    loading,
    refreshUser,
    setUser,
    isAdmin,
    isAuthor,
    isPendingContributor,
    isLoggedIn
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
